import { useCallback, useEffect, useMemo } from "react";
import { getHistoryActions } from "../../stores/historyStore";
import { getSceneState, useSceneStore } from "../../stores/sceneStore";
import { useSelectionStore } from "../../stores/selectionStore";
import { clearSelectedVisualMeshes } from "../../visual-mesh-multi-selection";
import { findGeomNode } from "../../utils/geomSelection";
import { isAutoGeneratedConstraintXmlName } from "../../utils/constraintNames";
import { focusNewEntityInInspector } from "../../utils/focusNewEntityInInspector";
import {
  activateMoveGizmoForDuplicatedBody,
  activateMoveGizmoForDuplicatedSelection,
  collisionGeomSelectionKeyAfterDuplicate,
  duplicateCollisionGeomFromKey,
  duplicateGeomFromKey,
  duplicateSiteGrouped,
  geomSelectionKeyAfterDuplicate,
  runDuplicateBodySelectionGrouped,
  runDuplicateBodySubtreeGrouped,
} from "../../utils/sceneDuplicateCommands";
import {
  clearPendingAutoParentForSelection,
  getPendingAutoParentForSelection,
} from "../../utils/pendingAutoParentDeletion";
import {
  canDeleteAutoParentBodyForItem,
  getGeomUuidFromSelectionKey,
  isTextEditingTarget,
} from "./inspectorTargetUtils";

export function useInspectorDuplicateDelete({
  activePanel,
  clearAllSelections,
  isPhysicsRunning,
  pendingVisualMeshParentBodyUuid,
  resolvedSelectedGeom,
  sceneActions,
  selectedActuator,
  selectedBody,
  selectedBodyExists,
  selectedBodies,
  selectedCamera,
  selectedEquality,
  selectedEqualityRecord,
  selectedGeom,
  selectedInertial,
  selectedJoint,
  selectedJointRecord,
  selectedLight,
  selectedSiteRecord,
  selectedTendon,
  selectedTendonRecord,
  selectedVisualMeshIds,
  setPendingVisualMeshParentBodyUuid,
}) {
  const showInspectorDuplicate =
    activePanel === "body" ||
    activePanel === "object" ||
    activePanel === "geom" ||
    activePanel === "visualGeom" ||
    activePanel === "joint" ||
    activePanel === "site" ||
    activePanel === "equality" ||
    activePanel === "tendon" ||
    activePanel === "multiBody" ||
    activePanel === "multiMesh";

  const inspectorDuplicateDisabled = useMemo(() => {
    if (isPhysicsRunning) return true;
    if (activePanel === "body" || activePanel === "object") {
      return !selectedBody || !selectedBodyExists;
    }
    if (activePanel === "geom") {
      const geom = resolvedSelectedGeom?.geom;
      return !selectedGeom || !geom || geom.visualOnly === true;
    }
    if (activePanel === "visualGeom") {
      const geom = resolvedSelectedGeom?.geom;
      return !selectedGeom || !geom || geom.visualOnly !== true;
    }
    if (activePanel === "joint") {
      const joint = selectedJoint ? selectedJointRecord : null;
      return !joint || joint.kind === "free";
    }
    if (activePanel === "site") {
      return !selectedSiteRecord?.uuid;
    }
    if (activePanel === "equality") {
      return !selectedEquality || !selectedEqualityRecord;
    }
    if (activePanel === "tendon") {
      return !selectedTendon || !selectedTendonRecord;
    }
    if (activePanel === "multiBody") {
      return !Array.isArray(selectedBodies) || selectedBodies.length < 2;
    }
    if (activePanel === "multiMesh") {
      return !selectedVisualMeshIds || selectedVisualMeshIds.size < 2;
    }
    return true;
  }, [
    activePanel,
    isPhysicsRunning,
    resolvedSelectedGeom,
    selectedBody,
    selectedBodyExists,
    selectedBodies,
    selectedEquality,
    selectedEqualityRecord,
    selectedGeom,
    selectedJoint,
    selectedJointRecord,
    selectedSiteRecord,
    selectedTendon,
    selectedTendonRecord,
    selectedVisualMeshIds,
  ]);

  const handleInspectorDuplicate = useCallback(() => {
    if (inspectorDuplicateDisabled) return false;
    if (activePanel === "body" || activePanel === "object") {
      if (!selectedBody) return false;
      const newUuid = runDuplicateBodySubtreeGrouped(sceneActions, selectedBody, false);
      if (newUuid) {
        const source = activePanel === "object" ? "sceneTreeObject" : "scene";
        focusNewEntityInInspector(source, () => useSelectionStore.getState().selectBody(newUuid), {
          duplicateChromeFlash: true,
        });
        activateMoveGizmoForDuplicatedBody(newUuid);
      }
      return true;
    }
    if (activePanel === "multiBody") {
      if (!Array.isArray(selectedBodies) || selectedBodies.length < 2) return false;
      const newUuids = runDuplicateBodySelectionGrouped(sceneActions, selectedBodies);
      if (newUuids.length > 0) {
        focusNewEntityInInspector(
          "scene",
          () => useSelectionStore.getState().setSelectedBodies(newUuids),
          {
            duplicateChromeFlash: true,
          }
        );
        activateMoveGizmoForDuplicatedBody(newUuids[0]);
      }
      return true;
    }
    if (activePanel === "geom") {
      const bodies = useSceneStore.getState().bodies;
      const newUuid = duplicateCollisionGeomFromKey(sceneActions, bodies, selectedGeom);
      const key = collisionGeomSelectionKeyAfterDuplicate(selectedGeom, newUuid);
      if (key) {
        focusNewEntityInInspector("scene", () => useSelectionStore.getState().selectGeom(key), {
          duplicateChromeFlash: true,
        });
        activateMoveGizmoForDuplicatedSelection();
      }
      return true;
    }
    if (activePanel === "visualGeom") {
      const bodies = useSceneStore.getState().bodies;
      const newUuid = duplicateGeomFromKey(sceneActions, bodies, selectedGeom);
      const key = geomSelectionKeyAfterDuplicate(selectedGeom, newUuid);
      if (key) {
        focusNewEntityInInspector(
          "scene",
          () => {
            clearSelectedVisualMeshes();
            useSelectionStore.getState().selectGeom(key);
          },
          {
            duplicateChromeFlash: true,
          }
        );
        activateMoveGizmoForDuplicatedSelection();
      }
      return true;
    }
    if (activePanel === "multiMesh") {
      const meshIds = new Set(Array.from(selectedVisualMeshIds || []).map(String));
      if (meshIds.size < 2) return false;
      const bodies = useSceneStore.getState().bodies;
      const visualGeomKeys = [];
      Object.values(bodies || {}).forEach((body) => {
        if (!body?.uuid || !Array.isArray(body.geoms)) return;
        body.geoms.forEach((geom) => {
          if (geom?.visualOnly !== true || !geom?.uuid || !geom.mesh) return;
          if (meshIds.has(String(geom.mesh))) {
            visualGeomKeys.push(`${body.uuid}:${String(geom.uuid)}`);
          }
        });
      });
      if (visualGeomKeys.length === 0) return false;
      const newKeys = [];
      const historyActions = getHistoryActions();
      historyActions.beginGroup(getSceneState());
      try {
        visualGeomKeys.forEach((geomKey) => {
          const newUuid = duplicateGeomFromKey(sceneActions, bodies, geomKey);
          const key = geomSelectionKeyAfterDuplicate(geomKey, newUuid);
          if (key) newKeys.push(key);
        });
      } finally {
        historyActions.endGroup(getSceneState());
      }
      if (newKeys.length > 0) {
        focusNewEntityInInspector(
          "scene",
          () => {
            clearSelectedVisualMeshes();
            useSelectionStore.getState().selectGeom(newKeys[0]);
          },
          {
            duplicateChromeFlash: true,
          }
        );
        activateMoveGizmoForDuplicatedSelection();
      }
      return true;
    }
    if (activePanel === "joint") {
      const joint = selectedJoint
        ? (useSceneStore.getState().joints?.[selectedJoint] ?? null)
        : null;
      if (!joint || joint.kind === "free") return false;
      const allBodies = useSceneStore.getState().bodies;
      const parentBodyUuid = Object.values(allBodies).find((body) =>
        body.joints?.includes(selectedJoint)
      )?.uuid;
      if (!parentBodyUuid) return false;
      const existingNames = new Set(
        Object.values(useSceneStore.getState().joints || {})
          .map((candidate) => candidate?.name)
          .filter(Boolean)
      );
      const base = `${joint.name || "joint"} copy`;
      let candidate = base;
      let suffix = 2;
      while (existingNames.has(candidate)) candidate = `${base} ${suffix++}`;
      const { uuid: _uuid, name: _name, ...rest } = joint;
      const newJointUuid = sceneActions.addJoint({ ...rest, name: candidate }, parentBodyUuid);
      if (newJointUuid) {
        focusNewEntityInInspector(
          "scene",
          () => useSelectionStore.getState().selectJoint(newJointUuid),
          {
            duplicateChromeFlash: true,
          }
        );
      }
      return true;
    }
    if (activePanel === "site") {
      const sourceSiteUuid = selectedSiteRecord?.uuid;
      if (!sourceSiteUuid) return false;
      const newUuid = duplicateSiteGrouped(sceneActions, sourceSiteUuid);
      if (!newUuid) return false;
      focusNewEntityInInspector("scene", () => useSelectionStore.getState().selectSite(newUuid), {
        duplicateChromeFlash: true,
      });
      activateMoveGizmoForDuplicatedSelection();
      return true;
    }
    if (activePanel === "equality") {
      const equality = selectedEquality
        ? useSceneStore.getState().equality?.[selectedEquality]
        : null;
      if (!equality) return false;
      const name = equality.name?.trim?.();
      const newUuid = sceneActions.addEquality({
        ...equality,
        uuid: undefined,
        name:
          name && !isAutoGeneratedConstraintXmlName(equality.name)
            ? `${equality.name} copy`
            : undefined,
      });
      if (!newUuid) return false;
      focusNewEntityInInspector(
        "scene",
        () => useSelectionStore.getState().selectEquality(newUuid),
        {
          duplicateChromeFlash: true,
        }
      );
      return true;
    }
    if (activePanel === "tendon") {
      const tendon = selectedTendon ? useSceneStore.getState().tendons?.[selectedTendon] : null;
      if (!tendon) return false;
      const newUuid = sceneActions.addTendon({
        ...tendon,
        uuid: undefined,
        name: tendon.name ? `${tendon.name} copy` : "tendon copy",
        joints: Array.isArray(tendon.joints) ? tendon.joints.map((entry) => ({ ...entry })) : [],
      });
      if (!newUuid) return false;
      focusNewEntityInInspector("scene", () => useSelectionStore.getState().selectTendon(newUuid), {
        duplicateChromeFlash: true,
      });
      return true;
    }
    return false;
  }, [
    activePanel,
    inspectorDuplicateDisabled,
    sceneActions,
    selectedBody,
    selectedBodies,
    selectedEquality,
    selectedGeom,
    selectedJoint,
    selectedSiteRecord,
    selectedTendon,
    selectedVisualMeshIds,
  ]);

  const inspectorDeleteLabel = useMemo(() => {
    switch (activePanel) {
      case "camera":
        return "Delete camera";
      case "light":
        return "Delete light";
      case "equality":
        return "Delete constraint";
      case "tendon":
        return "Delete tendon";
      case "actuator":
        return "Delete actuator";
      case "joint":
        return "Delete joint";
      case "body":
        return "Delete body";
      case "object":
        return "Delete entire object";
      case "site":
        return "Delete site";
      case "geom":
        return "Delete collision";
      case "visualGeom":
        return "Delete visual";
      case "inertial":
        return "Delete inertial";
      default:
        return "Delete";
    }
  }, [activePanel]);

  const inspectorDeleteDisabled = useMemo(() => {
    if (!activePanel) return true;
    if (isPhysicsRunning) return true;
    switch (activePanel) {
      case "camera":
        return !selectedCamera;
      case "light":
        return !selectedLight;
      case "equality":
        return !selectedEquality;
      case "tendon":
        return !selectedTendon;
      case "actuator":
        return !selectedActuator;
      case "joint":
        return !selectedJoint;
      case "body":
      case "object":
        return !selectedBody;
      case "site":
        return !selectedSiteRecord?.uuid;
      case "geom":
        return !selectedGeom;
      case "visualGeom":
        return !selectedGeom && !pendingVisualMeshParentBodyUuid;
      case "inertial": {
        const str = String(selectedInertial || "");
        return str.indexOf(":") <= 0;
      }
      default:
        return true;
    }
  }, [
    activePanel,
    isPhysicsRunning,
    pendingVisualMeshParentBodyUuid,
    selectedActuator,
    selectedBody,
    selectedCamera,
    selectedEquality,
    selectedGeom,
    selectedInertial,
    selectedJoint,
    selectedLight,
    selectedSiteRecord,
    selectedTendon,
  ]);

  const handleInspectorDelete = useCallback(() => {
    if (isPhysicsRunning) return;
    const deleteAutoParentIfStillPristine = (itemType, itemUuid, deleteItem) => {
      const parentBodyUuid = getPendingAutoParentForSelection(itemType, itemUuid);
      const shouldDeleteParent = canDeleteAutoParentBodyForItem(
        getSceneState().bodies || {},
        parentBodyUuid,
        itemType,
        itemUuid
      );
      if (!shouldDeleteParent) {
        deleteItem();
        clearPendingAutoParentForSelection(itemType, itemUuid);
        return;
      }
      const historyActions = getHistoryActions();
      historyActions.beginGroup(getSceneState());
      try {
        deleteItem();
        sceneActions.removeBody(parentBodyUuid);
      } finally {
        historyActions.endGroup(getSceneState());
        clearPendingAutoParentForSelection(itemType, itemUuid);
      }
    };

    switch (activePanel) {
      case "camera":
        if (selectedCamera) sceneActions.removeCamera(selectedCamera);
        break;
      case "light":
        if (selectedLight) sceneActions.removeLight(selectedLight);
        break;
      case "equality":
        if (selectedEquality) sceneActions.removeEquality(selectedEquality);
        break;
      case "tendon":
        if (selectedTendon) sceneActions.removeTendon(selectedTendon);
        break;
      case "actuator":
        if (selectedActuator) sceneActions.removeActuator(selectedActuator);
        break;
      case "joint":
        if (selectedJoint) {
          deleteAutoParentIfStillPristine("joint", selectedJoint, () =>
            sceneActions.removeJoint(selectedJoint)
          );
        }
        break;
      case "body":
        if (selectedBody) sceneActions.removeBody(selectedBody);
        break;
      case "object":
        if (selectedBody) sceneActions.removeBodyObject(selectedBody);
        break;
      case "site": {
        const site = selectedSiteRecord;
        if (site?.uuid) {
          deleteAutoParentIfStillPristine("site", site.uuid, () =>
            sceneActions.removeSite(site.uuid)
          );
        }
        break;
      }
      case "geom":
      case "visualGeom": {
        if (!selectedGeom && pendingVisualMeshParentBodyUuid) {
          setPendingVisualMeshParentBodyUuid(null);
          break;
        }
        if (!selectedGeom) return;
        const resolved = findGeomNode(useSceneStore.getState().bodies, selectedGeom);
        if (!resolved?.bodyUuid || !resolved.geom?.uuid) return;
        deleteAutoParentIfStillPristine("geom", getGeomUuidFromSelectionKey(selectedGeom), () =>
          sceneActions.removeGeom(resolved.bodyUuid, String(resolved.geom.uuid))
        );
        break;
      }
      case "inertial": {
        const str = String(selectedInertial || "");
        const sepIdx = str.indexOf(":");
        const bodyUuid = sepIdx > 0 ? str.slice(0, sepIdx) : null;
        if (bodyUuid) sceneActions.removeInertia(bodyUuid);
        break;
      }
      default:
        return;
    }
    clearAllSelections();
  }, [
    activePanel,
    clearAllSelections,
    isPhysicsRunning,
    pendingVisualMeshParentBodyUuid,
    sceneActions,
    selectedActuator,
    selectedBody,
    selectedCamera,
    selectedEquality,
    selectedGeom,
    selectedInertial,
    selectedJoint,
    selectedLight,
    selectedSiteRecord,
    selectedTendon,
    setPendingVisualMeshParentBodyUuid,
  ]);

  useEffect(() => {
    const handleDeleteKey = (event) => {
      if (event.defaultPrevented || event.key !== "Delete") return;
      if (inspectorDeleteDisabled || isTextEditingTarget(event.target)) return;

      event.preventDefault();
      handleInspectorDelete();
    };

    window.addEventListener("keydown", handleDeleteKey);
    return () => {
      window.removeEventListener("keydown", handleDeleteKey);
    };
  }, [handleInspectorDelete, inspectorDeleteDisabled]);

  return {
    handleInspectorDelete,
    handleInspectorDuplicate,
    inspectorDeleteDisabled,
    inspectorDeleteLabel,
    inspectorDuplicateDisabled,
    showInspectorDuplicate,
  };
}
